/**
 * Why a node the cloud says is connected does not answer this browser by its
 * name (D16: `https://n-<id>.<tenant>.<node domain>`), and what to do about
 * it — the hint the connectivity panel and a site's server list show.
 *
 * A browser does not say WHY a request failed: a name that does not resolve,
 * a certificate it refuses and a connection refused all surface as the same
 * `TypeError` (`network`); only a request that hangs is told apart
 * (`timeout`). So the hint combines what the probe saw with two facts the
 * cloud knows (`GET /status`):
 *
 *   sameNetwork   the caller's public address is the one the node's site link
 *                 comes from — so the browser is PROBABLY on the node's LAN
 *                 (false: probably not; null: unknown)
 *   certTrusted   the certificate the cloud delivered to the node is one
 *                 browsers trust (Let's Encrypt production, not staging)
 *
 * and decides, in this order:
 *
 *   node answers, or the cloud has no view / the node's link is not up   no hint
 *   sameNetwork false                                  other-network
 *   certTrusted false                                  untrusted-certificate
 *   the probe timed out                                no-answer      (the name resolved, nothing answered)
 *   the probe failed at once (network)                 dns-blocked    (probably: DNS rebinding protection)
 *
 * The last is a guess by elimination, and the copy says "probably": with a
 * trusted certificate and the same network, a name the router refuses to
 * resolve (DNS rebinding protection drops answers that point at private
 * addresses) is by far the likeliest cause of an instant failure — but a
 * browser refusing local network access fails the same way, and the copy
 * mentions it. docs/acp/sites.md#lan-dns explains each case.
 */
import type { StatusNodeWire } from '@acp/api-contract/statusRoutes.js';
import type { ConnectivityState, EndpointStatus } from './state.js';

export type ReachHintCase = 'dns-blocked' | 'untrusted-certificate' | 'no-answer' | 'other-network';

export type ReachHint = {
  readonly case: ReachHintCase;
  /** A few words: what is probably wrong. */
  readonly title: string;
  /** What it means and what to do, naming the node and, for DNS, the domain to allow. */
  readonly detail: string;
  /** The stage's node domain (`local.dev.acp.mimir.tv`): what a router exception names; null when the URL has none. */
  readonly nodeDomain: string | null;
};

/** Where the hints are explained, as the SPA names docs (a path in the repo). */
export const LAN_DNS_DOCS = 'docs/acp/sites.md#lan-dns';

/** The stage's node domain from a node's API URL: `https://n-<id>.<tenant>.<domain>` → `<domain>`; null for an address or another shape. */
export function nodeDomainOf(apiUrl: string): string | null {
  let host: string;
  try {
    host = new URL(apiUrl).hostname;
  } catch {
    return null;
  }
  const labels = host.split('.');
  if (labels.length < 4 || !/^[ns]-[a-z0-9]+$/.test(labels[0] ?? '')) return null;
  return labels.slice(2).join('.');
}

/** The hint for one watched endpoint, given what the cloud says about the node at its URL (null: nothing known). */
export function reachHint(status: EndpointStatus, node: StatusNodeWire | null): ReachHint | null {
  if (status.endpoint.kind !== 'node' || status.reach !== 'down' || node === null || node.linkState !== 'connected') return null;
  const who = status.endpoint.label;
  const nodeDomain = nodeDomainOf(status.endpoint.apiUrl);
  if (node.sameNetwork === false) {
    return {
      case: 'other-network',
      title: 'Probably not on the studio’s network',
      detail: `This browser reaches the internet from a different address than ${who} does, so it is probably on another network. The server answers only on its own LAN: connect to the studio network (or its VPN) to work with it directly. The cloud keeps working meanwhile.`,
      nodeDomain,
    };
  }
  if (node.certTrusted === false) {
    return {
      case: 'untrusted-certificate',
      title: 'The server’s certificate is not trusted',
      detail: `${who} serves a certificate from a test authority browsers refuse (Let’s Encrypt staging). On a stage that issues from Let’s Encrypt production the cloud replaces it by itself (its daily sweep); on one that issues from staging it stays untrusted — use the cloud, or accept the browser’s warning for the server’s name.`,
      nodeDomain,
    };
  }
  if (status.failure === 'timeout') {
    return {
      case: 'no-answer',
      title: 'The server does not answer',
      detail: `${who}’s name probably resolves, but nothing answered in time: a firewall, a guest or isolated Wi-Fi (client isolation) or a VLAN between this computer and the server, or a changed LAN address. Check that this computer can reach the server’s LAN address.`,
      nodeDomain,
    };
  }
  if (status.failure !== 'network') return null;
  const domain = nodeDomain ?? 'the servers’ domain';
  return {
    case: 'dns-blocked',
    title: 'The server’s name probably does not resolve on this network',
    detail: `Your router’s DNS is probably blocking names that point at private addresses (DNS rebinding protection), so ${who}’s name finds nothing here. Add an exception for ${domain} on the router, or use a public DNS resolver such as 1.1.1.1 on this computer. (A browser refusing local network access fails the same way: check this site’s permissions.)`,
    nodeDomain,
  };
}

/** The cloud's view of the node at `apiUrl`, or null. */
function statusNodeAt(state: ConnectivityState, apiUrl: string): StatusNodeWire | null {
  for (const site of state.cloudView?.sites ?? []) for (const node of site.nodes) if (node.apiUrl === apiUrl) return node;
  return null;
}

/** The hint for a watched endpoint in `state` (the panel's rows). */
export function hintFor(state: ConnectivityState, status: EndpointStatus): ReachHint | null {
  return reachHint(status, statusNodeAt(state, status.endpoint.apiUrl));
}

/** Every hint in `state`, by the node's id (a site's server list). */
export function reachHintsByNode(state: ConnectivityState): Readonly<Record<string, ReachHint>> {
  const out: Record<string, ReachHint> = {};
  for (const site of state.cloudView?.sites ?? []) {
    for (const node of site.nodes) {
      const status = state.endpoints.find((s) => s.endpoint.apiUrl === node.apiUrl);
      const hint = status === undefined ? null : reachHint(status, node);
      if (hint !== null) out[node.nodeId] = hint;
    }
  }
  return out;
}
